import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ToasterSeverities } from "@core/enums/toaster-severities";
import { DialogHelper } from "@core/helpers/dialog-helper";
import { FiscalRegime } from "@core/models/fiscal-regime.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { Product, TProductsPaginated } from "@core/models/product.model";
import { ProductKind } from "@core/models/product-kind.model";
import { ProductVersions } from "@core/models/product-versions.model";
import { ProductsInfo } from "@core/models/products-info.model";
import { NotificationService } from "@core/services/notification.service";
import { ProductService } from "@core/services/product.service";
import { TPagination } from "@custom-types/sorting";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";

import * as productActions from "./product.actions";
import { SET_PRODUCTS_LOADING_STATUS } from "./product.actions";
import { SELECT_PRODUCTS_PAGINATION } from "./product.selectors";

@Injectable()
export class ProductEffects {
  loadProducts$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.LOAD_PRODUCTS),
      withLatestFrom(this.store.select(SELECT_PRODUCTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            activeVersions?: boolean;
            pagination?: Partial<TPagination> | undefined;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(SET_PRODUCTS_LOADING_STATUS({ loading: true }));
          // Dropdown consumers pass activeVersions (never pagination) and read
          // the dedicated selectableProducts slice; the Products screen passes
          // pagination and reads products. Routing each to its own slice keeps
          // the screen's search from leaking into the dropdowns.
          const isSelectableLoad = !!action.activeVersions;
          // Selectable (dropdown) loads always fetch the full list. The grid's
          // own loads keep the stored page (e.g. refresh after a save).
          const paginationToUse =
            action.pagination ?? (isSelectableLoad ? undefined : pagination);
          return this.productService
            .getProducts$(undefined, action.activeVersions, paginationToUse)
            .pipe(
              switchMap(
                (
                  response: Array<Product> | TProductsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_PRODUCTS_LOADING_STATUS({ loading: false })
                  );
                  if (isSelectableLoad) {
                    return of(
                      productActions.SET_SELECTABLE_PRODUCTS({
                        productList: Array.isArray(response) ? response : [],
                      })
                    );
                  }
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      productActions.SET_PRODUCTS({
                        productList: data,
                      }),
                      productActions.SET_PRODUCTS_PAGINATION({ pagination })
                    );
                  }
                  return of(
                    productActions.SET_PRODUCTS({ productList: response })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                if (isSelectableLoad) {
                  return of(
                    productActions.SET_SELECTABLE_PRODUCTS({ productList: [] }),
                    SET_PRODUCTS_LOADING_STATUS({ loading: false })
                  );
                }
                return of(
                  productActions.SET_PRODUCTS({ productList: [] }),
                  productActions.SET_PRODUCTS_PAGINATION({
                    pagination: undefined,
                  }),
                  SET_PRODUCTS_LOADING_STATUS({ loading: false })
                );
              })
            );
        }
      )
    );
  });

  loadCompanyProducts$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.LOAD_COMPANY_PRODUCTS),
      mergeMap(
        (action: {
          companyProducts?: boolean;
          activeVersions?: boolean;
        }): Observable<Action> => {
          this.store.dispatch(SET_PRODUCTS_LOADING_STATUS({ loading: true }));
          return this.productService
            .getProducts$(action.companyProducts, action.activeVersions)
            .pipe(
              switchMap(
                (
                  productList: Array<Product> | TProductsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    SET_PRODUCTS_LOADING_STATUS({ loading: false })
                  );
                  return of(
                    productActions.SET_SELECTABLE_PRODUCTS({
                      productList: Array.isArray(productList)
                        ? productList
                        : [],
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(SET_PRODUCTS_LOADING_STATUS({ loading: false }));
              })
            );
        }
      )
    );
  });

  loadFunds$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.LOAD_FUNDS),
      mergeMap((): Observable<Action> => {
        return this.productService.getFunds$().pipe(
          switchMap((fundsList: Array<FundWithRelated>) => {
            return of(productActions.SET_FUNDS({ fundsList: fundsList }));
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  loadFiscalRegime$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.LOAD_FISCAL_REGIME),
      mergeMap((): Observable<Action> => {
        return this.productService.getFiscalRegime$().pipe(
          switchMap((fiscalRegimeList: Array<FiscalRegime>) => {
            return of(
              productActions.SET_FISCAL_REGIME({
                fiscalRegimeList: fiscalRegimeList,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  saveProduct$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.SAVE_PRODUCT),
      mergeMap(
        (action: {
          product: Partial<Product>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_PRODUCTS_LOADING_STATUS({ loading: true }));
          return this.productService
            .saveProduct$(action.product, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_PRODUCTS_LOADING_STATUS({ loading: false })
                );
                // The service creates (POST) when the id is empty and
                // updates (PUT) otherwise.
                this.notificationService.show(
                  ToasterSeverities.Success,
                  action.id === "" ? "PRODUCT_CREATED" : "PRODUCT_UPDATED",
                  undefined,
                  { name: action.product.name ?? "" }
                );
                return of(productActions.LOAD_PRODUCTS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "PRODUCT_SAVE_ERROR",
                  response,
                  { name: action.product.name ?? "" }
                );
                return of(SET_PRODUCTS_LOADING_STATUS({ loading: false }));
              })
            );
        }
      )
    );
  });

  saveProductVersion$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.SAVE_PRODUCT_VERSION),
      mergeMap(
        (action: {
          productVersion: Partial<ProductVersions>;
          id: string | undefined;
        }): Observable<Action> => {
          this.store.dispatch(SET_PRODUCTS_LOADING_STATUS({ loading: true }));
          return this.productService
            .saveProductVersion$(action.productVersion, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_PRODUCTS_LOADING_STATUS({ loading: false })
                );
                // Updating an existing version (non-empty id) triggers a
                // confirmation-email flow, so tell the user to await it;
                // a new/copied version (empty id) just saves.
                const isUpdate = !!action.id;
                this.notificationService.show(
                  ToasterSeverities.Success,
                  isUpdate
                    ? "PRODUCT_VERSION_UPDATED_WAIT_EMAIL"
                    : "PRODUCT_VERSION_CREATED"
                );
                return of(productActions.LOAD_PRODUCTS({}));
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "PRODUCT_VERSION_SAVE_ERROR",
                  response
                );
                return of(SET_PRODUCTS_LOADING_STATUS({ loading: false }));
              })
            );
        }
      )
    );
  });

  loadProductsInfo$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.LOAD_PRODUCTS),
      mergeMap((): Observable<Action> => {
        return this.productService.getProductsInfo$().pipe(
          switchMap((productsInfo: ProductsInfo): Observable<Action> => {
            return of(
              productActions.SET_PRODUCTS_INFO({ productsInfo: productsInfo })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  loadProductKind$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.LOAD_PRODUCT_KIND),
      mergeMap((): Observable<Action> => {
        return this.productService.getProductKind$().pipe(
          switchMap(
            (productKindList: Array<ProductKind>): Observable<Action> => {
              return of(
                productActions.SET_PRODUCT_KIND({
                  productKindList: productKindList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  loadNonDeviatedCostsCount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(productActions.LOAD_NON_DEVIATED_COSTS_COUNT),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.productService.getNonDeviatedCostsCount$(action.id).pipe(
          switchMap((nonDeviatedCostsCount: number): Observable<Action> => {
            return of(
              productActions.SET_NON_DEVIATED_COSTS_COUNT({
                nonDeviatedCostsCount: nonDeviatedCostsCount,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private productService: ProductService,
    private notificationService: NotificationService,
    private store: Store,
    private dialog: DialogHelper
  ) {}
}
